<template>
  <div class="input-step">
    <div class="input-step__field">
      <label id="term-input-label" class="input-step__label">{{ store.config.termInputLabel }}</label>

      <TermTagInput
        :terms="store.terms"
        :input-text="store.inputText"
        :placeholder="termPlaceholder"
        :term-count-text="termCountText"
        aria-labelledby="term-input-label"
        @update-input-text="store.inputText = $event"
        @remove-term="store.removeTerm($event)"
        @commit="store.commitInput($event)"
      />
    </div>

    <div class="input-step__divider">
      <span class="input-step__divider-text">{{ store.labels.orDivider }}</span>
    </div>

    <!-- Keyed on openGeneration so the dropzone's local UI state (drag-over,
         file-spec panel) starts fresh on every modal open. -->
    <FileDropzone
      :key="store.openGeneration"
      :file="store.termFile"
      :dropzone-text="store.config.dropzoneText"
      :file-spec-html="store.config.fileSpecHtml"
      :test-id="TestId.assignSpecialties.dropzone"
      @file-selected="store.handleFileChange($event)"
      @file-removed="store.removeFile()"
    />

    <div class="input-step__field">
      <!-- `.attr` sets through the attribute channel; a bare `:value` is re-applied over the pick. -->
      <sp-select
        class="input-step__mode-select"
        :label="store.config.modeLabel"
        :items.prop="modeOptions"
        :value.attr="store.mode"
        required
        :data-testid="TestId.assignSpecialties.modeSelect"
        @input="handleModeInput"
      ></sp-select>
    </div>

    <div class="input-step__field">
      <SpecialtyPicker />
    </div>

    <div v-if="store.clientControlEnabled" class="input-step__field">
      <sp-switch
        v-model="store.enableClientSpecialties"
        class="input-step__switch"
        :label="store.flagToggleLabel"
        :data-testid="TestId.assignSpecialties.clientControlToggle"
      ></sp-switch>
    </div>

    <div class="input-step__error" role="alert">
      {{ store.errorMessage }}
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";
import { firstDetail } from "#ce/utils/events";
import FileDropzone from "#ce/apps/SpAdmin/shared/components/FileDropzone.ce.vue";
import TermTagInput from "./TermTagInput.ce.vue";
import SpecialtyPicker from "./SpecialtyPicker.ce.vue";
import { useAssignSpecialtiesStore } from "#ce/apps/SpAdmin/SpAssignSpecialtiesApp/store/assign-specialties";
import { pluralizeTemplate } from "#ce/apps/SpAdmin/shared/utils/pluralize";

const store = useAssignSpecialtiesStore();

const termPlaceholder = computed(() => (store.terms.length === 0 ? store.labels.termInputPlaceholder : ""));
const termCountText = computed(() =>
  store.terms.length > 0 ? pluralizeTemplate(store.labels.termsEntered, store.terms.length) : "\u00A0",
);
const modeOptions = computed(() => [
  { title: store.config.modeAddLabel, value: "add" },
  { title: store.config.modeReplaceLabel, value: "replace" },
]);

function handleModeInput(event) {
  const value = firstDetail(event);

  if (value) {
    store.mode = value;
  }
}
</script>

<style scoped lang="scss">
.input-step {
  display: flex;
  flex-direction: column;

  &__field {
    display: flex;
    flex-direction: column;
    gap: var(--sp-ref-scale-8);
    margin-top: var(--sp-ref-scale-16);
  }

  &__label {
    font-size: var(--sp-sys-typescale-body-font-size, 1rem);
    font-weight: var(--sp-sys-form-field-label-font-weight, 600);
    color: var(--sp-sys-color-on-surface-variant, #666);
  }

  &__switch {
    font-weight: var(--sp-sys-form-field-label-font-weight, 600);
  }

  &__divider {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-ref-scale-6);
    padding: var(--sp-ref-scale-2) 0;
    margin-bottom: var(--sp-ref-scale-24);

    &::before,
    &::after {
      content: "";
      flex: 0 1 25%;
      height: 1px;
      background-color: var(--sp-sys-color-outline, #ccc);
    }
  }

  &__divider-text {
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);
    color: var(--sp-sys-color-on-surface-variant, #666);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: var(--sp-sys-font-weight-bold, 600);
    flex-shrink: 0;
  }

  &__error {
    color: var(--sp-sys-color-error, #d32f2f);
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);
    line-height: 1.25rem;
    min-height: 1.25rem;
    margin-top: var(--sp-ref-scale-4);
  }
}
</style>
